import type { CategoryResponseInfo, ReviewReviewResponse, BranchData, RegionRegionInfoData } from '@/types/api'
import { facebookLangMap, instagramLangMap } from '@/constants/links'

export type SeoSettingName = 'default' | 'region' | 'category' | 'branch' | null

type BranchSettingPayload = {
  branch: ComputedRef<BranchData | undefined>
  productComments: Ref<ReviewReviewResponse | null>
}
type CategorySettingPayload = {
  category: ComputedRef<CategoryResponseInfo | undefined>
}
type RegionSettingPayload = {
  regionInfo: ComputedRef<RegionRegionInfoData | undefined>
}

const DEFAULT_SETTING = {
  titleKey: 'index_og_title',
  descriptionKey: 'all_og_desc',
  image: 'https://cdn.myfunnow.com/web/og_images/eatigo_ogImage.png',
  url: 'https://eatigo.com/',
}

export function useEtgSeoSetting(name?: SeoSettingName, payload?: unknown) {
  switch (name) {
    case 'region':
      return useRegionSetting(payload as RegionSettingPayload)
    case 'category':
      return useCategorySetting(payload as CategorySettingPayload)
    case 'branch':
      return useBranchSetting(payload as BranchSettingPayload)
    default:
      return useBasicSetting()
  }
}

const useBasicSetting = () => {
  const { t } = useI18n()
  const config = useRuntimeConfig()
  const i18nHead = useLocaleHead({})

  const seoTitlePrefix = getTitlePrefix()
  const seoTitle = t(DEFAULT_SETTING.titleKey)
  const seoDescription = t(DEFAULT_SETTING.descriptionKey)

  useHead({
    htmlAttrs: i18nHead.value.htmlAttrs,
    link: [...(i18nHead.value.link || []), { rel: 'icon', type: 'image/x-icon', href: '/favicon.ico' }],
    meta: [...(i18nHead.value.meta ?? [])],
  })
  useSeoMeta({
    title: seoTitlePrefix + seoTitle,
    ogTitle: seoTitle,
    description: seoDescription,
    ogDescription: seoDescription,
    ogImage: DEFAULT_SETTING.image,
    ogUrl: DEFAULT_SETTING.url,
    fbAppId: config.public.fbAppId,
    ogType: 'website',
    twitterCard: 'summary_large_image',
  })
  useOrganizationJsonLd()
  useWebsiteJsonLd()
}

const useRegionSetting = ({ regionInfo }: RegionSettingPayload) => {
  const { t } = useI18n()
  const route = useRoute()
  const rid = route.params.rid as string

  const seoTitlePrefix = getTitlePrefix()
  const seoTitle = computed(() =>
    regionInfo.value?.name ? t('meta_title_region', { region_name: regionInfo.value.name }) : t(DEFAULT_SETTING.titleKey)
  )
  const seoDescription = computed(() => regionInfo.value?.meta_description || t(DEFAULT_SETTING.descriptionKey))

  useSeoMeta({
    title: () => seoTitlePrefix + seoTitle.value,
    ogTitle: () => seoTitle.value,
    description: () => seoDescription.value,
    ogDescription: () => seoDescription.value,
    ogUrl: () => `${DEFAULT_SETTING.url}regions/${rid}`,
  })
}

const useCategorySetting = ({ category }: CategorySettingPayload) => {
  const { t } = useI18n()
  const route = useRoute()
  const rid = route.params.rid as string
  const cid = (route.params.cid || route.params.tid) as string

  const seoTitlePrefix = getTitlePrefix()
  const seoTitle = computed(() =>
    category.value?.page_title ? t('meta_title_category', { category_name: category.value.page_title }) : t(DEFAULT_SETTING.titleKey)
  )
  const seoDescription = computed(() => category.value?.meta_desc || t(DEFAULT_SETTING.descriptionKey))

  useSeoMeta({
    title: () => seoTitlePrefix + seoTitle.value,
    ogTitle: () => seoTitle.value,
    description: () => seoDescription.value,
    ogDescription: () => seoDescription.value,
    ogUrl: () => `${DEFAULT_SETTING.url}regions/${rid}/categories/${cid}`,
  })
}

const useBranchSeoInfo = (branch: BranchSettingPayload['branch']) => {
  const { t } = useI18n()
  const route = useRoute()
  const config = useRuntimeConfig()
  const DESC_MAX_LENGTH = 150

  const cuisineName = computed(() => branch.value?.cuisine_tags?.[0]?.name ?? '')

  const seoTitle = computed(() =>
    t('meta_title_branch', {
      branch_name: branch.value?.name,
      primary_cuisine: cuisineName.value,
    })
  )
  const seoDesc = computed(() => {
    const desc = branch.value?.intro || t(DEFAULT_SETTING.descriptionKey)

    return desc.length > DESC_MAX_LENGTH ? `${desc.slice(0, DESC_MAX_LENGTH - 3)}...` : desc
  })
  const seoImg = computed(() => (branch.value?.cover_image ? `${config.public.funnowCdnBaseUrl}${branch.value.cover_image}` : DEFAULT_SETTING.image))
  const ogUrl = computed(() => DEFAULT_SETTING.url + route.fullPath.replace(/^\//, ''))

  return { cuisineName, seoTitle, seoDesc, seoImg, ogUrl }
}

const useBranchSetting = (payload: BranchSettingPayload) => {
  const { branch } = payload
  const { seoTitle, seoDesc, seoImg, ogUrl } = useBranchSeoInfo(branch)

  if (!branch.value) return

  const seoTitlePrefix = getTitlePrefix()

  useSeoMeta({
    title: () => seoTitlePrefix + seoTitle.value,
    ogTitle: () => seoTitle.value,
    description: () => seoDesc.value,
    ogDescription: () => seoDesc.value,
    ogImage: () => seoImg.value,
    ogUrl: () => ogUrl.value,
  })
  useRestaurantJsonLd(payload)
}

// JSON-LD components
const useOrganizationJsonLd = () => {
  const { t } = useI18n()
  const { regionId } = useRegion()

  useJsonld({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Eatigo',
    url: DEFAULT_SETTING.url,
    sameAs: [facebookLangMap[regionId.value], instagramLangMap[regionId.value]].filter((url): url is string => !!url),
    logo: `${DEFAULT_SETTING.url}images/logo-red.png`,
    image: DEFAULT_SETTING.image,
    description: t(DEFAULT_SETTING.descriptionKey),
  })
}

const useWebsiteJsonLd = () => {
  const { t } = useI18n()
  const { regionId } = useRegion()

  useJsonld({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: t(DEFAULT_SETTING.titleKey),
    author: 'Zoek Inc.',
    url: DEFAULT_SETTING.url,
    sameAs: [facebookLangMap[regionId.value], instagramLangMap[regionId.value]].filter((url): url is string => !!url),
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${DEFAULT_SETTING.url}search?keyword={keyword}`,
      },
      // @ts-expect-error: 有時間再來解這個錯誤，先 skip
      'query-input': `required name=keyword`,
    },
  })
}

const useRestaurantJsonLd = ({ branch, productComments }: BranchSettingPayload) => {
  const { cuisineName, seoImg } = useBranchSeoInfo(branch)

  const priceRange = Array.from({ length: branch.value?.avg_price_range || 1 }, () => '$').join('')

  const reviews = computed(() => {
    const result = productComments.value?.comments?.map(comment => {
      const [date] = comment.comment_time?.split(' ') ?? []

      return {
        '@type': 'Review' as const,
        author: comment.rated_by,
        datePublished: date,
        reviewBody: comment.description,
        reviewRating: {
          '@type': 'Rating' as const,
          bestRating: '5',
          ratingValue: comment.rating || 1, // Old reviews might have 0 rating, set to 1 to avoid invalid schema
          worstRating: '1',
        },
      }
    })

    return result
  })

  useJsonld(() => ({
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    image: seoImg.value,
    address: {
      '@type': 'PostalAddress',
      streetAddress: branch.value?.address,
      addressLocality: branch.value?.area,
      addressRegion: branch.value?.region_name,
    },
    name: branch.value?.name,
    ...(productComments.value?.avg_rating && productComments.value?.total_count
      ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: productComments.value.avg_rating,
            reviewCount: productComments.value.total_count,
          },
        }
      : {}),
    geo: {
      '@type': 'GeoCoordinates',
      latitude: branch.value?.lat,
      longitude: branch.value?.lng,
    },
    priceRange,
    review: reviews.value,
    servesCuisine: cuisineName.value,
  }))
}

// utils
const getTitlePrefix = () => {
  const config = useRuntimeConfig()
  return config.public.configEnv === 'prod' ? '' : `(${config.public.configEnv}) `
}
